import React, { useState, useEffect, useCallback } from 'react';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { getShipToPartiesCountForTransactionAccount } from '@atlassian/temp-proxies/src/partner-purchase-center/ccp-client/ccp';
import { downloadAsCSV } from '@css-xp/ppc-utils';
import { errorNotificationBuilder, NotificationTypes, useNotification } from '@css-xp/base';
import { failureMessage } from '@css-xp/ppc-common';
import {
  FetchShipToPartiesForTransactionAccountPpcQuery,
  FetchShipToPartiesForTransactionAccountPpcQueryVariables,
  PartnerSearchOptions,
} from '@css-xp/ppc-graphtypes';
import {
  extractPriceEligibility,
  getTaxExemptStatus,
} from '@css-xp/ppc-sold-to-address-table/src/sold-to-address-table';
import { useGetAggregateCcpList } from '@css-xp/ppc-custom-hooks';
import { fetchShipToPartiesForTransactionAccountPPC } from '../../../../queries';

export const useCustomersListCSV = (
  id: string,
  searchOptions?: PartnerSearchOptions,
  isSoldToAddressArchivalEnabled?: boolean,
  isRedesignFastFollowEnabled?: boolean
) => {
  const { notificationSent, sendErrorNotification, setNotificationSent } = useNotification();
  const [shipToPartiesCountData, setShipToPartiesCountData] = useState({ count: null, loading: false });
  const notifyError = () => {
    if (!notificationSent) {
      const notificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader(`We're unable to download the CSV file`)
        .withMessage(failureMessage())
        .withDismissAfter(5000)
        .build();
      sendErrorNotification(notificationOptions);
      setNotificationSent(true);
    }
  };

  const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);
  const { isLoading, getCcpData: loadCustomerList, error } = useGetAggregateCcpList<
    FetchShipToPartiesForTransactionAccountPpcQuery,
    FetchShipToPartiesForTransactionAccountPpcQueryVariables
  >(
    ppcBffGraphqlClient,
    fetchShipToPartiesForTransactionAccountPPC,
    {
      id: id,
      shipToPartyOptions: {
        pageOptions: {
          cursor: null,
          size: 100,
        },
        searchOptions: searchOptions,
      },
    },
    {
      totalCount: shipToPartiesCountData.count,
      paths: {
        dataPath: 'data.transactionAccount.shipToParty.nodes',
        cursorPath: 'data.transactionAccount.shipToParty.pageInfo.endCursor',
        hasNextPagePath: 'data.transactionAccount.shipToParty.pageInfo.hasNextPage',
      },
    }
  );

  if (error) {
    notifyError();
  }

  const getCSVHeaders = () => {
    const csvHeaders = [
      'Customer Id',
      'Customer',
      'Address line 1',
      'Address line 2',
      'City',
      'State',
      'Country',
      'Postal Code',
      'Pricing plan eligibility',
      'Tax exempt',
    ];

    if (isRedesignFastFollowEnabled) {
      csvHeaders.push('Tax ID');
    }

    return csvHeaders;
  };

  const getCSVRow = (node: any) => {
    const csvRow = [
      node?.id,
      node?.name ?? '-',
      node?.addressLine1 ?? '-',
      node?.addressLine2 ?? '-',
      node?.city ?? '-',
      node?.state ?? '-',
      node?.country ?? '-',
      node?.postcode ?? '-',
      extractPriceEligibility(node),
      getTaxExemptStatus(node),
    ];

    if (isRedesignFastFollowEnabled) {
      const taxIds = node?.taxIds?.map((taxId: any) => taxId?.id || '').join(', ');

      csvRow.push(taxIds || '-');
    }

    return csvRow;
  };

  const fetchCustomerList = useCallback(async () => {
    if (shipToPartiesCountData.count !== null) {
      const customersList = await loadCustomerList();
      if (customersList) {
        //sorting by name and addressLine1 to get the list in proper format
        customersList.sort((a, b) => {
          const nameComparison = (a?.name ?? '').localeCompare(b?.name ?? '') ?? 0;
          if (nameComparison !== 0) return nameComparison;
          // for same names, sort them by addressLine1 since that's how we're doing it on the UI as well to maintain the consistency.
          return (a?.addressLine1 ?? '').localeCompare(b?.addressLine1 ?? '') ?? 0;
        });
        downloadAsCSV({
          fileName: 'Customers-list',
          header: getCSVHeaders(),
          data: customersList.map(getCSVRow),
        });
      }
    }
  }, [shipToPartiesCountData]);

  useEffect(() => {
    fetchCustomerList();
  }, [shipToPartiesCountData]);

  const downloadCustomersListCSV = async () => {
    try {
      setShipToPartiesCountData({ count: null, loading: true });
      const { count } = await getShipToPartiesCountForTransactionAccount(
        id,
        searchOptions,
        isSoldToAddressArchivalEnabled
      );
      setShipToPartiesCountData({ count, loading: false });
    } catch (err) {
      setShipToPartiesCountData({ count: null, loading: false });
      notifyError();
    }
  };
  return { downloadCustomersListCSV, customersListLoading: shipToPartiesCountData.loading || isLoading };
};
